import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, User, Calendar, FileText, GraduationCap, Award, MessageSquare,
  Star, Grid, TrendingUp, BookOpen, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { DailyQuote } from "../components/home/DailyQuote";
import { AttendanceStreak } from "../components/common/AttendanceStreak";
import { FeeTrackerWidget } from "../components/common/FeeTrackerWidget";

export const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/portal"); return; }
    if (user.user_type === "parent") { navigate("/parent-portal"); return; }
    if (user.user_type === "teacher") { navigate("/teacher-dashboard"); return; }
    if (user.user_type === "admin") { navigate("/admin-dashboard"); return; }
    fetchDashboardData();
    fetchAnnouncements();
  }, [user, navigate]);

  const fetchAnnouncements = async () => {
    try {
      const data = await apiCall("GET", "/announcements");
      setAnnouncements(data);
    } catch (err) { console.error("Failed to fetch announcements:", err); }
  };

  const fetchDashboardData = async () => {
    try {
      const data = await apiCall("GET", `/dashboard/student/${user.id}`, null, token);
      setDashboardData(data);
    } catch (err) { console.error("Failed to fetch dashboard data:", err); }
    setLoading(false);
  };

  const getPriorityBadge = (priority) => {
    const styles = { high: "bg-red-100 text-red-600", normal: "bg-blue-100 text-blue-600", low: "bg-green-100 text-green-600" };
    return styles[priority] || styles.normal;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="student-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
          <div className="grid grid-cols-3 h-48">
            {user?.user_type === "parent" ? (
              <>
                <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/01izmww1_WhatsApp%20Image%202026-01-20%20at%206.40.16%20AM%20%281%29.jpeg" alt="Computer class" className="w-full h-full object-cover" />
                <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/c9j5lav4_WhatsApp%20Image%202026-01-20%20at%206.40.16%20AM.jpeg" alt="Cooking class" className="w-full h-full object-cover" />
                <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/lrohmhpm_WhatsApp%20Image%202026-01-20%20at%206.48.57%20AM.jpeg" alt="Library visit" className="w-full h-full object-cover" />
              </>
            ) : (
              <>
                <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/g7pdvxgd_WhatsApp%20Image%202026-01-20%20at%206.40.13%20AM.jpeg" alt="Classroom" className="w-full h-full object-cover" />
                <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/geytdxji_WhatsApp%20Image%202026-01-20%20at%206.48.57%20AM.jpeg" alt="Students in library" className="w-full h-full object-cover" />
                <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/1wz8s9af_WhatsApp%20Image%202026-01-20%20at%206.48.59%20AM%20%281%29.jpeg" alt="Field trip" className="w-full h-full object-cover" />
              </>
            )}
          </div>
        </div>

        <Card className="bg-white mb-8 border-2 border-[#002147]">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Bell size={24} /> School Announcements
            </CardTitle>
            <CardDescription className="text-gray-300">
              Important updates and news from the school
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-6 grid grid-cols-5 gap-2 rounded-lg overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/4yo6717e_WhatsApp%20Image%202026-01-20%20at%206.40.16%20AM%20-%20Copy%20%282%29.jpeg" alt="School activity" className="w-full h-24 object-cover rounded" />
              <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/5gap90gc_WhatsApp%20Image%202026-01-20%20at%206.40.17%20AM%20%282%29%20-%20Copy.jpeg" alt="School event" className="w-full h-24 object-cover rounded" />
              <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/yk4zyyop_WhatsApp%20Image%202026-01-20%20at%206.40.17%20AM%20%283%29%20-%20Copy.jpeg" alt="Students" className="w-full h-24 object-cover rounded" />
              <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/hcz9tjsd_WhatsApp%20Image%202026-01-20%20at%206.40.18%20AM.jpeg" alt="School activity" className="w-full h-24 object-cover rounded" />
              <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/eytp32rm_WhatsApp%20Image%202026-01-20%20at%206.48.56%20AM%20-%20Copy.jpeg" alt="Students learning" className="w-full h-24 object-cover rounded" />
            </div>
            <ScrollArea className="h-[250px]">
              <div className="space-y-4">
                {announcements.map((announcement) => (
                  <Card key={announcement.id} className="border-l-4 border-l-[#FF6B00] bg-gradient-to-r from-yellow-50 to-orange-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getPriorityBadge(announcement.priority)}>{announcement.priority}</Badge>
                        <Badge variant="outline">{announcement.category}</Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-[#002147]">{announcement.title}</h3>
                      <p className="text-gray-600 mt-1">{announcement.content}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        By {announcement.author_name} - {new Date(announcement.created_at).toLocaleDateString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
                {announcements.length === 0 && (
                  <div className="text-center py-8">
                    <Bell className="mx-auto mb-4 text-gray-400" size={48} />
                    <p className="text-gray-500">No announcements yet</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <div className="mb-8">
          <DailyQuote />
          <h1 className="text-3xl font-bold text-[#002147]">Welcome back, {user?.first_name}!</h1>
          <p className="text-gray-600">
            {user?.user_type === "parent" ? "Monitor your child's academic progress" : "Here's an overview of your academic progress"}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-gray-500">GPA</p><p className="text-3xl font-bold text-[#002147]">{dashboardData?.gpa?.toFixed(2) || "3.75"}</p></div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center"><TrendingUp className="text-green-600" size={24} /></div>
              </div>
              <Progress value={((dashboardData?.gpa || 3.75) / 4) * 100} className="mt-4" />
            </CardContent>
          </Card>
          <Card className="bg-white card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-gray-500">Attendance</p><p className="text-3xl font-bold text-[#002147]">{dashboardData?.attendance_summary?.attendance_rate?.toFixed(0) || "96"}%</p></div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><Calendar className="text-blue-600" size={24} /></div>
              </div>
              <Progress value={dashboardData?.attendance_summary?.attendance_rate || 96} className="mt-4" />
            </CardContent>
          </Card>
          <Card className="bg-white card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-gray-500">Assignments</p><p className="text-3xl font-bold text-[#002147]">{dashboardData?.pending_assignments || 3}</p></div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center"><FileText className="text-orange-600" size={24} /></div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Pending submissions</p>
            </CardContent>
          </Card>
          <Card className="bg-white card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-gray-500">Messages</p><p className="text-3xl font-bold text-[#002147]">{dashboardData?.unread_messages || 2}</p></div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"><MessageSquare className="text-purple-600" size={24} /></div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Unread messages</p>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-8 bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Grid size={20} /> Quick Links</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="flex flex-col gap-2">
                <Button variant="outline" onClick={() => navigate("/resources/enrollment-application")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white">
                  <GraduationCap size={24} /><span className="text-sm font-medium">Admissions</span>
                </Button>
                <Button onClick={() => navigate("/fees")} className="bg-[#FF6B00] hover:bg-[#E65C00] text-white text-xs py-2">Pay Fees</Button>
              </div>
              <Button variant="outline" onClick={() => navigate("/resources")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><BookOpen size={24} /><span className="text-sm font-medium">Academics</span></Button>
              <Button variant="outline" onClick={() => navigate("/gallery")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><Award size={24} /><span className="text-sm font-medium">Athletics</span></Button>
              <Button variant="outline" onClick={() => navigate("/gallery")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><Star size={24} /><span className="text-sm font-medium">Arts</span></Button>
              <Button variant="outline" onClick={() => navigate("/resources")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><FileText size={24} /><span className="text-sm font-medium">Resources</span></Button>
              <Button variant="outline" onClick={() => navigate("/my-id-card")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#FF6B00] hover:text-white border-[#FF6B00]/30" data-testid="my-id-card-btn"><CreditCard size={24} /><span className="text-sm font-medium">My ID Card</span></Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          <AttendanceStreak />
          <FeeTrackerWidget />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Card className="bg-white">
            <CardHeader><CardTitle className="flex items-center gap-2"><Calendar size={20} /> Class Schedule</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {(dashboardData?.classes || []).map((cls, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div><p className="font-medium">{cls.name}</p><p className="text-xs text-gray-500">{cls.schedule}</p></div>
                      <Badge variant="outline">{cls.room}</Badge>
                    </div>
                  ))}
                  {(!dashboardData?.classes || dashboardData.classes.length === 0) && (
                    <p className="text-center text-gray-500 py-4">No classes scheduled</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardHeader><CardTitle className="flex items-center gap-2"><FileText size={20} /> Upcoming Assignments</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {(dashboardData?.upcoming_assignments || []).map((assignment, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" onClick={() => navigate(`/assignment/${assignment.id}`)}>
                      <div>
                        <p className="font-medium">{assignment.title}</p>
                        <p className="text-xs text-gray-500">{assignment.subject} - Due: {new Date(assignment.due_date).toLocaleDateString()}</p>
                      </div>
                      <Badge>{assignment.max_points} pts</Badge>
                    </div>
                  ))}
                  {(!dashboardData?.upcoming_assignments || dashboardData.upcoming_assignments.length === 0) && (
                    <p className="text-center text-gray-500 py-4">No upcoming assignments</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
